import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import type { BaseLocationEvent } from '../../domain/models/locations/BaseLocationEvent';
import { ReactComponent as GiftIcon } from '../../icons/gift.svg';
import { ReactComponent as LeaderIcon } from '../../icons/leader.svg';
import EventDDayBadge from '../../presentation/components/event-detail/EventDDayBadge';
import { getSiteType } from '@pocapan/shared/utils/SiteTypes';

const SITE_TYPE_ICON = [
  {
    id: 'birthday_cafe_icon',
    url: '/assets/icons/offlineLocation/siteType/birthday_cafe_icon.png'
  },
  {
    id: 'birthday_cafe_icon_click',
    url: '/assets/icons/offlineLocation/siteType/birthday_cafe_icon_click.png'
  },
  { id: 'experience_space_icon', url: '/assets/icons/offlineLocation/siteType/exhibition_icon.png' },
  {
    id: 'experience_space_icon_click',
    url: '/assets/icons/offlineLocation/siteType/exhibition_icon_click.png'
  },
  {
    id: 'filming_location_icon',
    url: '/assets/icons/offlineLocation/siteType/filming_location_icon.png'
  },
  {
    id: 'filming_location_icon_click',
    url: '/assets/icons/offlineLocation/siteType/filming_location_icon_click.png'
  },
  { id: 'foodie_spot_icon', url: '/assets/icons/offlineLocation/siteType/foodie_spot_icon.png' },
  {
    id: 'foodie_spot_icon_click',
    url: '/assets/icons/offlineLocation/siteType/foodie_spot_icon_click.png'
  },
  { id: 'billboard_ad_icon', url: '/assets/icons/offlineLocation/siteType/billboard_ad_icon.png' },
  {
    id: 'billboard_ad_icon_click',
    url: '/assets/icons/offlineLocation/siteType/billboard_ad_icon_click.png'
  },
  { id: 'landmark_icon', url: '/assets/icons/offlineLocation/siteType/landmark_icon.png' },
  {
    id: 'landmark_icon_click',
    url: '/assets/icons/offlineLocation/siteType/landmark_icon_click.png'
  }
];

const EventInformation = ({
  event,
  siteType,
  hasPadding = false
}: { event: BaseLocationEvent; siteType: string; hasPadding?: boolean }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const badgeRef = useRef<HTMLSpanElement>(null);
  const [displayText, setDisplayText] = useState(event.name);

  useEffect(() => {
    const calculateTextLength = () => {
      const text = event.name;

      // Calculate text weight with language-specific weights
      const getTextWeight = (str: string) => {
        if (isEmpty(str)) {
          return 0;
        }

        let weight = 0;
        for (let i = 0; i < str.length; i++) {
          const char = str[i];

          // Korean characters (Hangul)
          if (/[\uAC00-\uD7A3\u3131-\u318E]/.test(char)) {
            weight += 2.2;
          }
          // Chinese characters (CJK)
          else if (/[\u4E00-\u9FFF\u3400-\u4DBF]/.test(char)) {
            weight += 2.1;
          }
          // Japanese characters (Hiragana, Katakana)
          else if (/[\u3040-\u309F\u30A0-\u30FF]/.test(char)) {
            weight += 2.0;
          }
          // Wide punctuation and symbols
          else if (/[\u3000-\u303F\uFF00-\uFFEF]/.test(char)) {
            weight += 1.8;
          }
          // English uppercase letters (slightly wider)
          else if (/[A-Z]/.test(char)) {
            weight += 1.2;
          }
          // Wide English letters (M, W)
          else if (/[MW]/.test(char)) {
            weight += 1.3;
          }
          // Narrow English letters (i, l, j, f, t)
          else if (/[iljft]/.test(char)) {
            weight += 0.7;
          }
          // Regular English letters and numbers
          else if (/[a-z0-9]/.test(char)) {
            weight += 1.0;
          }
          // Punctuation and symbols
          else {
            weight += 0.8;
          }
        }
        return weight;
      };

      const textWeight = getTextWeight(text);
      // More conservative limit for 2 lines with badge space
      // This accounts for roughly 20-22 Korean characters or 45-50 English characters
      const maxWeight = 35;

      if (textWeight <= maxWeight) {
        setDisplayText(text);
      } else {
        // Truncate text to fit within weight limit
        let truncatedText = '';
        let currentWeight = 0;
        const ellipsisWeight = 3; // Weight for "..."

                for (let i = 0; i < text.length; i++) {
          const char = text[i];
          let charWeight = 1.0;

          // Use same weight calculation as above
          if (/[\uAC00-\uD7A3\u3131-\u318E]/.test(char)) {
            charWeight = 2.2;
          } else if (/[\u4E00-\u9FFF\u3400-\u4DBF]/.test(char)) {
            charWeight = 2.1;
          } else if (/[\u3040-\u309F\u30A0-\u30FF]/.test(char)) {
            charWeight = 2.0;
          } else if (/[\u3000-\u303F\uFF00-\uFFEF]/.test(char)) {
            charWeight = 1.8;
          } else if (/[A-Z]/.test(char)) {
            charWeight = 1.1;
          } else if (/[MW]/.test(char)) {
            charWeight = 1.3;
          } else if (/[iljft]/.test(char)) {
            charWeight = 0.7;
          } else if (/[a-z0-9]/.test(char)) {
            charWeight = 1.0;
          } else {
            charWeight = 0.8;
          }

          if (currentWeight + charWeight + ellipsisWeight > maxWeight) {
            break;
          }

          truncatedText += char;
          currentWeight += charWeight;
        }

        setDisplayText(truncatedText + '...');
      }
    };

    calculateTextLength();
  }, [event.name]);

  return (
    <div className={`flex items-start space-x-[14px]${hasPadding ? ' px-[16px]' : ''}`}>
      <img
        src={event.baseImageUrl}
        alt="event_image"
        className="rounded-[6px] max-w-[65px] aspect-[65/90] object-cover"
      />

      <div className="flex flex-col justify-between h-[90px]">
        <div
          ref={containerRef}
          className="h-[40px] text-[15px] font-semibold text-main-light-1 dark:text-main-dark-1 leading-[20px] overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical]"
        >
          <span ref={textRef} className="mr-[4px]">
            {displayText}
          </span>
          <span ref={badgeRef} className="inline-block align-middle mb-[1.5px]">
            <EventDDayBadge
              startDate={event.startDate.toDateString()}
              endDate={event.endDate.toDateString()}
            />
          </span>
          {/* {pos.events.length !== 0 && (
            <span className="px-[5px] py-[2.5px] mb-[1.5px] rounded bg-secondary-light dark:bg-secondary-dark text-[10px] leading-[12px] text-onSecondary-light dark:text-onSecondary-dark">
              {dDay}
            </span>
          )} */}
        </div>

        <div>
          <div className="flex flex-row items-center text-main-light-4 dark:text-main-dark-4 text-[13px]">
            <img
              src={SITE_TYPE_ICON.find((icon) => icon.id === `${siteType}_icon`)?.url}
              alt="organizer_icon"
              className="mr-[4px] mb-[1px] w-[14px] h-[14px]"
            />
            <div className="text-[13px] font-normal leading-[16px]">
              {getSiteType(siteType).name}
            </div>

            <div className="w-[2px] h-[2px] bg-sub-light-2 dark:bg-sub-light-2 rounded-full mx-[5px]" />
            <LeaderIcon className="mr-[4px] mb-[1px] w-[14px] h-[14px] flex-shrink-0 fill-sub-light-1 dark:fill-sub-dark-1" />
            <div className="text-[13px] font-normal leading-[16px] max-w-[112px] truncate">
              {event.organizerTwitterId}
            </div>
          </div>

          <div className="flex flex-row items-center mt-[6px] text-main-light-4 dark:text-main-dark-4 text-[13px]">
            <GiftIcon className="mr-[4px] mb-[1px] w-[14px] h-[14px] fill-sub-light-1 dark:fill-sub-dark-1" />
            <div className="text-[13px] font-normal leading-[16px]">
              {isEmpty(event.goodsNames)
                ? t('EventInformation.emptyGoodsList')
                : event.goodsNames && event.goodsNames.length <= 2
                  ? event.goodsNames.join(', ')
                  : t('EventInformation.goodsList', {
                      goods: (event.goodsNames ?? []).slice(0, 2).join(', '),
                      count: Math.max((event.goodsNames?.length ?? 0) - 2, 0)
                    })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventInformation;
